import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LayoutGrid, CalendarDays, Settings, LogOut, Menu, X, BarChart3 } from 'lucide-react';

export default function Layout({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', label: 'Resources', icon: LayoutGrid },
    { to: '/my-bookings', label: 'My Bookings', icon: CalendarDays },
    { to: '/heatmap', label: 'Heatmap', icon: BarChart3 },
    ...(isAdmin ? [{ to: '/admin', label: 'Admin', icon: Settings }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <span className="font-heading text-lg font-bold tracking-tighter text-white">mr.polygon.rs</span>
              <span className="text-xs tracking-[0.2em] uppercase font-mono text-text-secondary hidden sm:inline">/bookings</span>
            </Link>

            {/* Desktop Nav */}
            <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
              {navItems.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  data-testid={`nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-2 px-3 py-1.5 text-sm font-body transition-all duration-200 ${
                    isActive(to)
                      ? 'text-white bg-white/10'
                      : 'text-text-secondary hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  {label}
                </Link>
              ))}
            </nav>

            {/* User / Logout */}
            <div className="hidden md:flex items-center gap-3">
              <span className="text-xs font-mono text-text-secondary" data-testid="nav-user-email">{user?.email}</span>
              <button
                onClick={handleLogout}
                data-testid="logout-button"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-text-secondary hover:text-white hover:bg-white/5 transition-all duration-200"
              >
                <LogOut size={14} strokeWidth={1.5} />
                Logout
              </button>
            </div>

            {/* Mobile menu toggle */}
            <button
              className="md:hidden p-2 text-text-secondary hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-black/80 backdrop-blur-xl" data-testid="mobile-nav">
            <nav className="px-4 py-3 space-y-1">
              {navItems.map(({ to, label, icon: Icon }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${label.toLowerCase().replace(/\s/g, '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 text-sm font-body transition-all ${
                    isActive(to)
                      ? 'text-white bg-white/10'
                      : 'text-text-secondary hover:text-white'
                  }`}
                >
                  <Icon size={16} strokeWidth={1.5} />
                  {label}
                </Link>
              ))}
              <div className="pt-2 border-t border-white/10">
                <span className="block px-3 py-1 text-xs font-mono text-text-secondary">{user?.email}</span>
                <button
                  onClick={handleLogout}
                  data-testid="mobile-logout-button"
                  className="flex items-center gap-3 px-3 py-2.5 text-sm text-text-secondary hover:text-white w-full"
                >
                  <LogOut size={16} strokeWidth={1.5} />
                  Logout
                </button>
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-14 min-h-screen">
        {children}
      </main>
    </div>
  );
}
